<template>
  <Menu as="div" class="relative inline-block text-left" v-slot="{ open }">
    <MenuButton
      class="relative h-8 inline-flex items-center px-3 py-2 shadow-sm text-sm font-medium rounded-md text-text-primary hover:bg-gray-700 bg-gray-800 border border-gray-700 focus:border-gray-700 focus:outline-none focus:ring-0"
      :class="open ? 'bg-gray-700 ring-1 ring-gray-600' : 'bg-gray-800'"
    >
      <div class="flex-1 text-left font-light flex gap-2 items-center">
        <span class="text-text-secondary">{{ label }}:</span>
        <span v-if="selectedItems.length === 0" class="text-text-secondary">{{ $t('common.all') }}</span>
        <span v-else-if="selectedItems.length <= 2" class="text-text-primary truncate max-w-[16rem]">{{ selectedItems.map((item) => item.name || item).join(', ') }}</span>
        <span v-else class="text-text-primary">{{ $t('shared.multi_select.selected_count', { n: selectedItems.length }) }}</span>
      </div>
      <ChevronDownIcon :class="open ? 'rotate-180' : ''" class="-mr-1 ml-2 h-4 w-4 transition-all duration-300" aria-hidden="true" />
    </MenuButton>

    <transition
      enter-active-class="transition ease-out duration-100"
      enter-from-class="transform opacity-0 scale-95"
      enter-to-class="transform opacity-100 scale-100"
      leave-active-class="transition ease-in duration-75"
      leave-from-class="transform opacity-100 scale-100"
      leave-to-class="transform opacity-0 scale-95"
    >
      <MenuItems
        class="absolute right-0 z-50 mt-2 w-64 origin-top-right divide-y divide-gray-100 rounded-md bg-gray-900 border border-gray-800 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none max-h-64 overflow-y-auto"
      >
        <div class="flex flex-col py-4 gap-4">
          <h1 class="text-text-primary px-4 font-medium">{{ $t('shared.multi_select.select', { label }) }}</h1>

          <!-- Search Input -->
          <div class="px-4">
            <input
              type="text"
              v-model="searchQuery"
              class="w-full bg-gray-800 border border-gray-700 rounded-md p-2 text-sm placeholder:text-text-secondary placeholder:font-light text-text-primary"
              :placeholder="$t('shared.multi_select.search', { label: label.toLowerCase() })"
              @keydown.space.stop
              @keyup.space.stop
              @keypress.space.stop
            />
          </div>

          <!-- Options List -->
          <div class="flex flex-col" @vue:mounted="resetOnOpen">
            <div
              v-for="item in filteredItems"
              :key="item"
              :class="[
                'group flex items-center py-2 text-sm w-full justify-start text-text-primary px-4 cursor-pointer hover:bg-gray-800',
              ]"
              @click="toggleItem(item)"
            >
              <span v-if="hasLogos" class="h-5 w-5 mr-2 shrink-0">
                <img v-if="logos[item]" :src="logos[item]" alt="" loading="lazy" class="h-5 w-5 object-contain" @error="$event.target.classList.add('invisible')" />
              </span>
              <span class="flex-1">{{ item.name || item }}</span>
              <input
                type="checkbox"
                :checked="isSelected(item)"
                class="h-4 w-4 text-primary-500 bg-gray-700 border-gray-600 rounded focus:ring-primary-500 focus:ring-2 ml-2"
                @click.stop
                @change="toggleItem(item)"
              />
            </div>

            <!-- No results -->
            <div v-if="filteredItems.length === 0" class="px-4 py-2 text-text-secondary text-sm">
              {{ $t('shared.multi_select.no_results', { label: label.toLowerCase() }) }}
            </div>
          </div>
        </div>
      </MenuItems>
    </transition>
  </Menu>
</template>

<script setup>
import { ref, computed } from 'vue';
import {
  Menu, MenuButton, MenuItems, MenuItem,
} from '@headlessui/vue';
import { ChevronDownIcon } from '@heroicons/vue/24/outline';

const props = defineProps({
  items: {
    type: Array,
    required: true,
    default: () => [],
  },
  selectedItems: {
    type: Array,
    required: true,
    default: () => [],
  },
  label: {
    type: String,
    required: true,
  },
  logos: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['update:selectedItems']);

const searchQuery = ref('');

const pinned = ref([]);

const resetOnOpen = () => {
  pinned.value = [...props.selectedItems];
  searchQuery.value = '';
};

const hasLogos = computed(() => Object.keys(props.logos).length > 0);

const orderedItems = computed(() => [
  ...props.items.filter((item) => pinned.value.includes(item)),
  ...props.items.filter((item) => !pinned.value.includes(item)),
]);

const filteredItems = computed(() => {
  if (!searchQuery.value) return orderedItems.value;
  return orderedItems.value.filter((item) => item.toLowerCase().includes(searchQuery.value.toLowerCase()));
});

const isSelected = (item) => props.selectedItems.includes(item);

const toggleItem = (item) => {
  const isCurrentlySelected = isSelected(item);
  let newSelectedItems;

  if (isCurrentlySelected) {
    // Remove item
    newSelectedItems = props.selectedItems.filter((selectedItem) => selectedItem !== item);
  } else {
    // Add item
    newSelectedItems = [...props.selectedItems, item];
  }

  emit('update:selectedItems', newSelectedItems);
};
</script>
